/**
 * Read-only backing of a Sandboxes v2 environment (Property → Environments).
 * The environment is synced from the application's `cofactor.sandbox.yaml`,
 * so its backing is shown here, never edited.
 */
import { Text } from '@chatsdet/ui';
import { useApplicationSandbox } from '../../../hooks/useApplicationSandbox';
import { formatEnvironmentUpdatedAt } from './propertyEnvironmentPresentation';

type SandboxV2EnvironmentFieldsProps = {
  applicationId: string;
  /** The definition environment name (`sandboxV2Environment`). */
  environment: string;
};

export function SandboxV2EnvironmentFields({
  applicationId,
  environment,
}: SandboxV2EnvironmentFieldsProps) {
  const settingQ = useApplicationSandbox(applicationId, true);
  const setting = settingQ.data;
  const newestPush = setting?.pushes?.[0] ?? null;

  return (
    <div className="space-y-1">
      <Text size="sm" weight="medium">
        Managed by cofactor.sandbox.yaml — environment{' '}
        <code className="text-xs">{environment}</code>
      </Text>
      {settingQ.isLoading ? (
        <Text size="xs" className="text-muted-foreground">
          Loading delivery…
        </Text>
      ) : settingQ.isError || !setting ? (
        <Text size="xs" className="text-destructive">
          {settingQ.error instanceof Error
            ? settingQ.error.message
            : 'Failed to load the application sandbox setting.'}
        </Text>
      ) : setting.delivery === 'github' ? (
        <Text size="xs" className="text-muted-foreground">
          GitHub: {setting.repo}
          {setting.definitionDir && setting.definitionDir !== '.' ? ` · ${setting.definitionDir}` : ''}
        </Text>
      ) : (
        <Text size="xs" className="text-muted-foreground">
          Pushed images
          {newestPush
            ? ` · latest ${newestPush.tags.length > 0 ? `${newestPush.tags.join(', ')} · ` : ''}${formatEnvironmentUpdatedAt(newestPush.createdAt)}`
            : ' · no pushes yet'}
        </Text>
      )}
    </div>
  );
}

SandboxV2EnvironmentFields.displayName = 'SandboxV2EnvironmentFields';
